import { Alpine } from '../../core/alpine-core.js';
import { apiFetch } from '../api.js';
import { canManageTournamentDoc, canDeleteTournamentDoc, canEndTournamentSessionDoc } from './tournament-access.js';

const PHASE_LABELS = {
  draft: 'Draft',
  registration: 'Registration open',
  drawn: 'Draw published',
  in_progress: 'In progress',
  completed: 'Ended',
};

Alpine.data('tournamentDetailPage', () => ({
  loading: true,
  deleting: false,
  ending: false,
  error: '',
  tournamentId: '',
  tournament: null,
  entries: [],

  canManage() {
    return canManageTournamentDoc(this.tournament, this.$store?.session?.me);
  },

  canDelete() {
    return canDeleteTournamentDoc(this.tournament, this.$store?.session?.me);
  },

  canEndSession() {
    return canEndTournamentSessionDoc(this.tournament, this.$store?.session?.me);
  },

  phaseLabel() {
    const ph = String(this.tournament?.phase || '').trim();
    return PHASE_LABELS[ph] || ph || '—';
  },

  formatLabel() {
    return String(this.tournament?.format || '').replace(/_/g, ' ') || '—';
  },

  entryTypeLabel() {
    const t = String(this.tournament?.entryType || 'player');
    if (t === 'team') return 'Teams';
    if (t === 'pair') return 'Pairs';
    return 'Players';
  },

  statusMessage() {
    const t = this.tournament;
    if (!t) return '';
    const ph = String(t.phase || '');
    if (t.hasBracket || ph === 'drawn' || ph === 'in_progress' || ph === 'completed') {
      if (ph === 'completed') return 'This tournament has ended.';
      if (ph === 'in_progress') return 'Matches are underway. Open the bracket to follow progress.';
      return 'The draw is ready. Open the bracket to see fixtures and results.';
    }
    if (ph === 'registration' || ph === 'draft') {
      if (this.canManage()) {
        return 'Add entrants in the builder, then generate the bracket when everyone is in.';
      }
      return 'You are entered in this tournament. The organiser has not published the draw yet — check back here for updates.';
    }
    return '';
  },

  statusTone() {
    const ph = String(this.tournament?.phase || '');
    if (this.tournament?.hasBracket || ['drawn', 'in_progress'].includes(ph)) return 'emerald';
    if (ph === 'completed') return 'neutral';
    return 'amber';
  },

  async init() {
    const path = String(globalThis.location.pathname || '').replace(/\/+$/, '') || '';
    const m = /\/tournaments\/([^/]+)$/.exec(path);
    if (!m || m[1] === 'new') {
      this.error = 'Invalid tournament URL';
      this.loading = false;
      return;
    }
    this.tournamentId = decodeURIComponent(m[1]);
    await this.load();
  },

  async load() {
    this.loading = true;
    this.error = '';
    try {
      const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        throw new Error(data.message || data.error || 'Tournament not found');
      }
      this.tournament = data.tournament || null;
      this.entries = Array.isArray(data.entries) ? data.entries : [];
    } catch (e) {
      this.error = e.message || 'Failed to load tournament';
      this.tournament = null;
      this.entries = [];
    } finally {
      this.loading = false;
    }
  },

  async deleteTournament() {
    if (!this.tournamentId || !this.canDelete()) return;
    const title = this.tournament?.title || 'this tournament';
    if (!globalThis.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    this.deleting = true;
    try {
      const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || data.error || 'Delete failed');
      this.$store?.toastr?.success?.('Tournament deleted');
      const router = globalThis.PineconeRouter;
      if (router?.navigate) router.navigate('/admin/tournaments');
      else globalThis.location.assign('/admin/tournaments');
    } catch (e) {
      this.$store?.toastr?.error?.(e.message || 'Delete failed');
    } finally {
      this.deleting = false;
    }
  },

  async endTournamentSession() {
    if (!this.tournamentId || !this.canEndSession()) return;
    const title = this.tournament?.title || 'this tournament';
    if (!globalThis.confirm(`End "${title}" now? Players will not be able to continue matches.`)) return;
    this.ending = true;
    try {
      const res = await apiFetch(`/tournaments/${encodeURIComponent(this.tournamentId)}/end`, { method: 'POST', body: {} });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || data.error || 'Could not end tournament');
      this.tournament = data.tournament || this.tournament;
      this.$store?.toastr?.success?.('Tournament ended');
    } catch (e) {
      this.$store?.toastr?.error?.(e.message || 'Could not end tournament');
    } finally {
      this.ending = false;
    }
  },
}));
